import { get, post, del, put } from './client';

const BASE_URL = process.env.REACT_APP_ATLANTIS_URL ?? 'https://dev-api.degentreasures.com/v1';

export const getPromos = async (Authorization = '') => {
  const res = await get(`${BASE_URL}/promos`, {
    Authorization,
  });
  return res?.payload;
};

export const getPromoById = async (promoId, Authorization = '') => {
  const res = await get(`${BASE_URL}/promos/${promoId}`, {
    Authorization,
  });
  return res?.payload;
};

export const createPromo = async (body, Authorization = '') => {
  const res = await post(`${BASE_URL}/promos`, { ...body }, { Authorization });
  return res?.data?.payload;
};

export const updatePromo = async (promoId, body, Authorization = '') => {
  const res = await put(`${BASE_URL}/promos/${promoId}`, { ...body }, { Authorization });
  return res?.data?.payload ?? res?.payload;
};

// TRU-1077: bind (or re-bind) the aegis RFP campaign a WAITLIST promo's wave
// prizes pay into. Boxes already granted keep the campaign sealed on them;
// only later grants read the new value. Atlantis proves the campaign is real
// and unexpired and refuses non-WAITLIST promos.
export const setPromoRfpCampaign = async (promoId, rfpCampaignId, Authorization = '') => {
  const res = await put(
    `${BASE_URL}/promos/${promoId}/rfp-campaign`,
    { rfpCampaignId },
    { Authorization }
  );
  return res?.data?.payload ?? res?.payload;
};

export const getPromoShopItems = async (promoId, Authorization = '') => {
  const res = await get(`${BASE_URL}/promos/${promoId}/items/all`, {
    Authorization,
  });
  return res?.payload;
};

export const getPromoShopItemsOfWallet = async (promoId, walletId, Authorization = '') => {
  const res = await get(`${BASE_URL}/wallets/${walletId}/promos/${promoId}/items?filter=NONE`, {
    Authorization,
  });
  return res?.payload;
};

export const createPromoShopItem = async (promoId, body, Authorization = '') => {
  const res = await post(`${BASE_URL}/promos/${promoId}/items`, { ...body }, { Authorization });
  return res?.data?.payload;
};

export const deletePromoShopItem = async (promoId, itemId, Authorization = '') => {
  const res = await del(`${BASE_URL}/promos/${promoId}/items/${itemId}`, { Authorization });
  return res?.data?.payload;
};

export const updatePromoShopItem = async (promoId, itemId, body, Authorization = '') => {
  const res = await put(
    `${BASE_URL}/promos/${promoId}/items/${itemId}`,
    { ...body },
    { Authorization }
  );
  return res?.payload;
};

// Purchasable flag only. Archive already sends `{ archived }` alone on this
// same item update. A dedicated enable route can replace this body later
// without the shop UI sending name, price, or image.
export const setPromoShopItemDisabled = async (promoId, itemId, disabled, Authorization = '') =>
  updatePromoShopItem(promoId, itemId, { disabled: disabled === true }, Authorization);

export const getRewardsOfItem = async (promoId, itemId, Authorization = '') => {
  const res = await get(`${BASE_URL}/promos/${promoId}/items/${itemId}/rewards`, {
    Authorization,
  });
  return res?.payload;
};

export const updateRewardsOfItem = async (promoId, itemId, body, Authorization = '') => {
  const res = await post(
    `${BASE_URL}/promos/${promoId}/items/${itemId}/rewards`,
    { ...body },
    { Authorization }
  );
  return res?.payload;
};

export const getRewardedBoxes = async (promoId, rewardId, Authorization = '') => {
  const res = await get(`${BASE_URL}/promos/${promoId}/rewards/${rewardId}/boxes`, {
    Authorization,
  });
  return res?.payload;
};

export const getBoxesByItem = async (promoId, itemId, Authorization = '') => {
  const res = await get(`${BASE_URL}/promos/${promoId}/items/${itemId}/boxes`, {
    Authorization,
  });
  return res?.payload;
};

// Admin VIP cycle-history lookup (TRU-875/876). Both endpoints are gated
// server-side to SECONDARY_LEGAL_WALLETS — a non-admin wallet gets a 403.
export const getVipCycles = async (months, Authorization = '') => {
  const res = await get(`${BASE_URL}/admin/vip/cycles?months=${months}`, {
    Authorization,
  });
  return res?.payload;
};

export const getVipCycleDistributions = async (cycleId, Authorization = '') => {
  const res = await get(`${BASE_URL}/admin/vip/cycles/${cycleId}/distributions`, {
    Authorization,
  });
  return res?.payload;
};

export const createDozerCoin = async (walletId, dozerCoinAmount, Authorization = '') => {
  const res = await post(
    `${BASE_URL}/dozer-coins`,
    { walletAddress: walletId, coinAmounts: dozerCoinAmount },
    { Authorization }
  );
  return res?.data?.payload;
};

export const consumeAllFragments = async (walletId, body = {}, Authorization = '') => {
  const res = await post(
    `${BASE_URL}/admin/wallets/${walletId}/fragments/consume-all`,
    { ...body },
    { Authorization }
  );
  return res?.data?.payload;
};

export const getAllShopHours = async (promoId, Authorization = '') => {
  const res = await get(`${BASE_URL}/promos/${promoId}/shop-open-hours/all`, { Authorization });
  return res?.payload;
};

export const createShopHour = async (promoId, body, Authorization = '') => {
  const res = await post(
    `${BASE_URL}/promos/${promoId}/shop-open-hours`,
    { ...body },
    { Authorization }
  );
  return res?.data?.payload;
};

export const deleteShopHour = async (promoId, shopHourId, Authorization = '') => {
  const res = await del(`${BASE_URL}/promos/${promoId}/shop-open-hours/${shopHourId}`, {
    Authorization,
  });
  return res?.data?.payload;
};

export const updateShopHour = async (promoId, shopHourId, body, Authorization = '') => {
  const res = await put(
    `${BASE_URL}/promos/${promoId}/shop-open-hours/${shopHourId}`,
    { ...body },
    { Authorization }
  );
  return res?.data?.payload;
};

// Site announcements (global front-page popup) — NOT promo-scoped.
// Reads are public; writes require the Cognito idToken (raw, no "Bearer " prefix).
export const getAllSiteAnnouncements = async (Authorization = '') => {
  const res = await get(`${BASE_URL}/site-announcements/all`, { Authorization });
  return res?.payload;
};

export const createSiteAnnouncement = async (body, Authorization = '') => {
  const res = await post(`${BASE_URL}/site-announcements`, { ...body }, { Authorization });
  return res?.data?.payload;
};

export const updateSiteAnnouncement = async (announcementId, body, Authorization = '') => {
  const res = await put(
    `${BASE_URL}/site-announcements/${announcementId}`,
    { ...body },
    { Authorization }
  );
  return res?.data?.payload;
};

export const deleteSiteAnnouncement = async (announcementId, Authorization = '') => {
  const res = await del(`${BASE_URL}/site-announcements/${announcementId}`, { Authorization });
  return res?.data?.payload;
};

const fileToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
  });
};

export const uploadItemImage = async (file, imageType, Authorization = '') => {
  try {
    const base64 = await fileToBase64(file);
    const res = await post(
      `${BASE_URL}/upload-image`,
      {
        image: base64,
        contentType: file.type,
        fileName: file.name,
        imageType,
      },
      { Authorization }
    );
    const imageUrl = res?.data?.payload?.imageUrl;
    if (!imageUrl) {
      throw new Error('Upload failed — no image URL returned.');
    }
    return imageUrl;
  } catch (error) {
    if (error.response?.status === 413) {
      throw new Error('File too large. Please use an image under 4.5MB.');
    }
    throw error;
  }
};

// Shop-hour video (DEGEN-1403): the MP4 rides the same base64 upload endpoint
// as images (4.5 MB cap, no processing). Admin exports the file small
// (target under 2 MB); the coin shop plays it as-is.
export const uploadShopVideo = async (file, Authorization = '') => {
  try {
    const video = await fileToBase64(file);
    const res = await post(
      `${BASE_URL}/upload-image`,
      {
        image: video,
        // The field only admits .mp4; declare it as such whatever the OS reported.
        contentType: 'video/mp4',
        fileName: file.name,
        imageType: 'shop-hour-video',
      },
      { Authorization }
    );
    const videoUrl = res?.data?.payload?.imageUrl;
    if (!videoUrl) {
      throw new Error('Upload failed — no video URL returned.');
    }
    return videoUrl;
  } catch (error) {
    if (error.response?.status === 413) {
      throw new Error('File too large. Please use a video under 4.5MB.');
    }
    const serverMessage = error.response?.data?.message;
    if (typeof serverMessage === 'string' && serverMessage) {
      throw new Error(serverMessage);
    }
    throw error;
  }
};
